import Modal from "../../components/Modal/Modal";

import React, { useState, useEffect } from "react";
import { get, put } from "../../api";
import Frame from "../../components/Frame";
import LoadingFrame from "../../components/LoadingFrame";
import Label from "../../components/Label";
import InputRow from "../../components/InputRow";
import FrameHeader from "../../components/FrameHeader";
import Space from "../../components/Space";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import {
  SENTINEL_MOBILE,
  SENTINEL_MOBILE_TRACKER,
  SENTINEL_TMA,
  SENTINEL_TRACKER,
  SENTINEL_V0,
  SENTINEL_V3,
  SENTINEL_V4,
  SENTINEL_V5,
  SENTINEL_V6,
  SENTINEL_V7,
  SENTINEL_V8,
  SENTINEL_V9,
} from "../../constants/sentinel_types";
import * as Statuses from "../../constants/statuses";
import styles from "./AdminPortalDevice.module.scss";

const DEVICE_TYPE_OPTIONS = [
  // { id: SENTINEL_MOBILE_TRACKER, label: "Mobile Tracker" },
  //{ id: SENTINEL_TMA, label: "TMA" },
  { id: SENTINEL_MOBILE, label: "Mobile" },
  { id: SENTINEL_TRACKER, label: "Tracker" },
  { id: SENTINEL_V0, label: "Impact Tracker V0" },
  { id: SENTINEL_V3, label: "Impact Tracker V3" },
  { id: SENTINEL_V4, label: "Impact Tracker V4" },
  { id: SENTINEL_V5, label: "Impact Tracker V5" },
  { id: SENTINEL_V6, label: "Impact Tracker V6" },
  { id: SENTINEL_V7, label: "Impact Tracker V7" },
  { id: SENTINEL_V8, label: "Impact Tracker V8" },
  { id: SENTINEL_V9, label: "Impact Tracker V9" },
];

function getDeviceTypeLabel(id) {
  const found = DEVICE_TYPE_OPTIONS.find((type) => type.id === id);
  return found ? found.label : id;
}

const STATUS_OPTIONS = Object.values(Statuses);

export default function AdminPortalDevice() {
  const [loading, setLoading] = useState(false);
  const [serial, setSerial] = useState("");
  const [devices, setDevices] = useState([]); // now an array
  const [selectedDeviceIdx, setSelectedDeviceIdx] = useState(null);
  const [editDevice, setEditDevice] = useState(null);
  const [originalDevice, setOriginalDevice] = useState(null); // Track original for change detection
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");

  useEffect(() => {
    async function fetchCustomers() {
      setCustomersLoading(true);
      setCustomersError("");
      try {
        const res = await get("/customers");
        // Defensive: check for array and valid objects
        if (Array.isArray(res.results) && res.results.length > 0) {
          setCustomers(res.results);
        } else {
          setCustomers([]);
          setCustomersError("No customers found.");
        }
      } catch (err) {
        setCustomers([]);
        setCustomersError("Failed to fetch customers.");
      } finally {
        setCustomersLoading(false);
      }
    }
    fetchCustomers();
  }, []);

  function getCustomerName(id) {
    const found = customers.find((c) => String(c.id) === String(id));
    return found ? found.name : id ? `ID: ${id}` : "Unknown";
  }

  async function getDevice() {
    if (!serial.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const res = await get(
        `/devices/serial/${encodeURIComponent(serial.trim())}`
      );
      // If API returns a list, use it directly; if not, wrap single device in array
      const deviceList = Array.isArray(res) ? res : res ? [res] : [];
      setDevices(deviceList);
      setSelectedDeviceIdx(null);
      setEditDevice(null);
    } catch (e) {
      setDevices([]);
      setSelectedDeviceIdx(null);
      setEditDevice(null);
      setError("No devices found or an error occurred.");
    } finally {
      setLoading(false);
    }
  }

  // Get default settings based on device type
  function getDefaultDeviceSettings(deviceTypeId) {
    switch (deviceTypeId) {
      case SENTINEL_MOBILE_TRACKER:
        return {
          deployment_frequency: 360,
          pulse_frequency: 360,
          sensitivity: "DISABLED"
        };
      case SENTINEL_TMA:
      case SENTINEL_MOBILE:
        return {
          deployment_frequency: 5,
          pulse_frequency: 60,
          sensitivity: "LOW"
        };
      case SENTINEL_TRACKER:
        return {
          deployment_frequency: 360,
          pulse_frequency: 360,
          sensitivity: "DISABLED"
        };
      case SENTINEL_V0:
      case SENTINEL_V3:
      case SENTINEL_V4:
      case SENTINEL_V5:
      case SENTINEL_V6:
      case SENTINEL_V7:
      case SENTINEL_V8:
      case SENTINEL_V9:
        // All Impact Tracker versions (V0-V9) use the same settings
        return {
          deployment_frequency: 360,
          pulse_frequency: 360,
          sensitivity: "MEDIUM"
        };
      default:
        return {
          deployment_frequency: 360,
          pulse_frequency: 360,
          sensitivity: "MEDIUM"
        };
    }
  }

  function handleEditChange(field, value) {
    setEditDevice((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  // Special handler for device type changes
  function handleDeviceTypeChange(newDeviceTypeId) {
    const defaults = getDefaultDeviceSettings(newDeviceTypeId);
    setEditDevice((prev) => ({
      ...prev,
      devicetype_id: newDeviceTypeId,
      deployment_frequency: defaults.deployment_frequency,
      pulse_frequency: defaults.pulse_frequency,
      sensitivity: defaults.sensitivity
    }));
  }

  async function saveDevice() {
    if (!editDevice) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      // Only send the fields defined in the C# Device model
      const devicePayload = {
        id: editDevice.id,
        additional_info: editDevice.additional_info,
        customer_id: editDevice.customer_id,
        devicetype_id: editDevice.devicetype_id,
        name: editDevice.name,
        search_text: editDevice.search_text,
        latitude: editDevice.latitude,
        longitude: editDevice.longitude,
        status: editDevice.status,
        active: editDevice.active,
        serial_number: editDevice.serial_number,
        imei: editDevice.imei,
        iccid: editDevice.iccid,
        firmware: editDevice.firmware,
        battery: editDevice.battery,
        signal: editDevice.signal,
        orientation: editDevice.orientation,
        temperature: editDevice.temperature,
        modem_firmware: editDevice.modem_firmware,
        gps_recalibration: editDevice.gps_recalibration,
        activation_code: editDevice.activation_code,
        should_update: editDevice.should_update,
        update_version: editDevice.update_version,
        update_filename: editDevice.update_filename,
        update_try_counter: editDevice.update_try_counter,
        pulse_frequency: editDevice.pulse_frequency,
        deployment_frequency: editDevice.deployment_frequency,
        sensitivity: editDevice.sensitivity,
        cell_tower_id: editDevice.cell_tower_id,
        cell_accuracy: editDevice.cell_accuracy,
        ai_key: editDevice.ai_key,
        damaged: editDevice.damaged
      };

      await put(`/devices/update/${editDevice.id}`, devicePayload);
      // Update the device in the list and original device
      setDevices((prev) =>
        prev.map((d, idx) => (idx === selectedDeviceIdx ? editDevice : d))
      );
      setOriginalDevice({ ...editDevice });
      setSaveSuccess(true);
    } catch (e) {
      setSaveError("Failed to update device.");
    } finally {
      setSaving(false);
    }
  }

  function handleSelectDevice(idx) {
    setSelectedDeviceIdx(idx);
    const device = { ...devices[idx] };
    setEditDevice(device);
    setOriginalDevice({ ...device }); // Store original for comparison
    setSaveSuccess(false);
    setSaveError("");
  }

  function getStatusClass(status) {
    if (!status) return styles.info;
    const s = status.toUpperCase();
    if (s === "OK" || s === "READY" || s === "DEPLOYED") return styles.ok;
    if (
      s.includes("WARNING") ||
      s.includes("MAINTENANCE") ||
      s.includes("INSPECTION") ||
      s.includes("MINOR")
    )
      return styles.warning;
    if (
      s.includes("ERROR") ||
      s.includes("CRITICAL") ||
      s.includes("INACTIVE") ||
      s.includes("EXPIRED") ||
      s.includes("MAJOR")
    )
      return styles.error;
    return styles.info;
  }

  function handleCloseModal() {
    setEditDevice(null);
    setOriginalDevice(null);
    setSaveSuccess(false);
    setSaveError("");
  }

  // Check if device has been modified
  function hasChanges() {
    if (!editDevice || !originalDevice) return false;
    return JSON.stringify(editDevice) !== JSON.stringify(originalDevice);
  }

  function formatASPDate(dateString) {
    if (!dateString) return "";
    // Handle /Date(1234567890-0000)/ format
    const match = /\/Date\((\d+)([+-]\d+)?\)\//.exec(dateString);
    if (match) {
      return new Date(Number(match[1])).toLocaleString();
    }
    return dateString;
  }

  return (
    <>
      <FrameHeader title="Device lookup" />
      <Frame>
        <InputRow>
          <Space size="s">
            <TextInput
              value={serial}
              placeholder="Enter serial number"
              onChange={(e) => setSerial(e.target.value)}
            />
          </Space>

          <Space>
            <Button onClick={getDevice} disabled={loading || !serial.trim()}>
              Search
            </Button>
          </Space>
        </InputRow>

        {loading && <LoadingFrame />}

        {error && <div className={styles.error}>{error}</div>}

        {customersLoading && <div>Loading customers...</div>}
        {customersError && <div className={styles.error}>{customersError}</div>}

        {devices.length > 0 && !loading && (
          <div>
            <div className={styles.deviceList}>
              <h4>Devices found:</h4>
              <div className={styles.deviceTable}>
                <div className={styles.headerRow}>
                  <div>Serial / Name</div>
                  <div>Type</div>
                  <div>Customer</div>
                  <div>Status</div>
                </div>
                {devices.map((d, idx) => (
                  <div
                    key={d.id}
                    className={`${styles.deviceRow} ${idx === selectedDeviceIdx ? styles.selected : ""
                      }`}
                    onClick={() => handleSelectDevice(idx)}
                  >
                    <div className={`${styles.cell} ${styles.serialName}`}>
                      {d.name || d.serial_number}
                    </div>
                    <div className={styles.cell}>
                      <span className={styles.typeBadge}>
                        {getDeviceTypeLabel(d.devicetype_id)}
                      </span>
                    </div>
                    <div className={styles.cell}>
                      {getCustomerName(d.customer_id)}
                    </div>
                    <div className={styles.cell}>
                      <span
                        className={`${styles.statusBadge} ${getStatusClass(
                          d.status
                        )}`}
                      >
                        {d.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {editDevice && (
              <Modal
                header={`Edit Device: ${editDevice.name || editDevice.serial_number}`}
                footer={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <Button
                      onClick={handleCloseModal}
                      style={{ background: "#9ca3af" }}
                    >
                      Close
                    </Button>
                    <Button onClick={saveDevice} disabled={saving || !hasChanges()}>
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                }
              >
                <div className={styles.devicePreview}>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveDevice();
                    }}
                    className={styles.deviceForm}
                  >
                    <div className={styles.sectionHeader}>
                      <h3>Identifiers</h3>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="ID" />
                      <TextInput value={editDevice?.id ?? ""} disabled />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Customer" />
                      <select
                        value={editDevice?.customer_id ?? ""}
                        onChange={(e) =>
                          handleEditChange("customer_id", Number(e.target.value))
                        }
                      >
                        <option value="" disabled>Select customer</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} (ID: {c.id})
                          </option>
                        ))}
                      </select>
                    </div>


                    <div className={styles.formGroup}>
                      <Label text="Device Type" />
                      <div className={styles.inputWithBadge}>
                        <span className={styles.typeDisplay}>
                          {getDeviceTypeLabel(editDevice?.devicetype_id)}
                        </span>
                        <select
                          value={editDevice?.devicetype_id ?? ""}
                          onChange={(e) =>
                            handleDeviceTypeChange(Number(e.target.value))
                          }
                        >
                          <option value="" disabled>Select type</option>
                          {DEVICE_TYPE_OPTIONS.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.label} (ID: {type.id})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Serial Number" />
                      <TextInput
                        value={editDevice?.serial_number ?? ""}
                        onChange={(e) =>
                          handleEditChange("serial_number", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="IMEI" />
                      <TextInput
                        value={editDevice?.imei || ""}
                        onChange={(e) => handleEditChange("imei", e.target.value)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="ICCID" />
                      <TextInput
                        value={editDevice?.iccid || ""}
                        onChange={(e) =>
                          handleEditChange("iccid", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Name" />
                      <TextInput
                        value={editDevice?.name || ""}
                        onChange={(e) => handleEditChange("name", e.target.value)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Search Text" />
                      <TextInput
                        value={editDevice?.search_text || ""}
                        onChange={(e) =>
                          handleEditChange("search_text", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Additional Info" />
                      <TextInput
                        value={editDevice?.additional_info || ""}
                        onChange={(e) =>
                          handleEditChange("additional_info", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.sectionHeader}>
                      <h3>Location</h3>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Latitude" />
                      <TextInput
                        type="number"
                        value={editDevice?.latitude ?? ""}
                        onChange={(e) =>
                          handleEditChange("latitude", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Longitude" />
                      <TextInput
                        type="number"
                        value={editDevice?.longitude ?? ""}
                        onChange={(e) =>
                          handleEditChange("longitude", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.sectionHeader}>
                      <h3>Status & Timing</h3>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Status" />
                      <div className={styles.inputWithBadge}>
                        <span className={styles.statusDisplay}>
                          {editDevice?.status}
                        </span>
                        <select
                          value={editDevice?.status || ""}
                          onChange={(e) =>
                            handleEditChange("status", e.target.value)
                          }
                        >
                          <option value="" disabled>Select status</option>
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                      <Label text="Active" />
                      <input
                        type="checkbox"
                        checked={!!editDevice?.active}
                        onChange={(e) =>
                          handleEditChange("active", e.target.checked)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Created Time" />
                      <TextInput
                        value={formatASPDate(editDevice?.created_time)}
                        disabled
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Status Updated Time" />
                      <TextInput
                        value={formatASPDate(editDevice?.status_updated_time)}
                        disabled
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Activation Code" />
                      <TextInput
                        value={editDevice?.activation_code || ""}
                        onChange={(e) =>
                          handleEditChange("activation_code", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Activation Time" />
                      <TextInput
                        value={formatASPDate(editDevice?.activation_time)}
                        disabled
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Customer Activation Time" />
                      <TextInput
                        value={formatASPDate(editDevice?.customer_activation_time)}
                        disabled
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Last Update Time" />
                      <TextInput
                        value={formatASPDate(editDevice?.last_update_time)}
                        disabled
                      />
                    </div>

                    <div className={styles.sectionHeader}>
                      <h3>Device Health</h3>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Battery" />
                      <TextInput
                        type="number"
                        value={editDevice?.battery ?? ""}
                        onChange={(e) =>
                          handleEditChange("battery", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Signal" />
                      <TextInput
                        type="number"
                        value={editDevice?.signal ?? ""}
                        onChange={(e) =>
                          handleEditChange("signal", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Temperature" />
                      <TextInput
                        type="number"
                        value={editDevice?.temperature ?? ""}
                        onChange={(e) =>
                          handleEditChange("temperature", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Orientation" />
                      <select
                        value={editDevice?.orientation || ""}
                        onChange={(e) =>
                          handleEditChange("orientation", e.target.value)
                        }
                      >
                        <option value="" disabled>Select orientation</option>
                        <option value="POSITION_FLAT">POSITION_FLAT</option>
                        <option value="POSITION_VERTICAL">POSITION_VERTICAL</option>
                        <option value="POSITION_HORIZONTAL">POSITION_HORIZONTAL</option>
                        <option value="POSITION_UNKNOWN">POSITION_UNKNOWN</option>
                      </select>
                    </div>

                    <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                      <Label text="Damaged" />
                      <input
                        type="checkbox"
                        checked={!!editDevice?.damaged}
                        onChange={(e) =>
                          handleEditChange("damaged", e.target.checked)
                        }
                      />
                    </div>

                    <div className={styles.sectionHeader}>
                      <h3>Firmware & Settings</h3>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Firmware" />
                      <TextInput
                        value={editDevice?.firmware || ""}
                        onChange={(e) =>
                          handleEditChange("firmware", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Modem Firmware" />
                      <TextInput
                        value={editDevice?.modem_firmware || ""}
                        onChange={(e) =>
                          handleEditChange("modem_firmware", e.target.value)
                        }
                      />
                    </div>

                    <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                      <Label text="GPS Recalibration" />
                      <input
                        type="checkbox"
                        checked={!!editDevice?.gps_recalibration}
                        onChange={(e) =>
                          handleEditChange("gps_recalibration", e.target.checked)
                        }
                      />
                    </div>

                    <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                      <Label text="Should Update" />
                      <input
                        type="checkbox"
                        checked={!!editDevice?.should_update}
                        onChange={(e) =>
                          handleEditChange("should_update", e.target.checked)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Update Version" />
                      <TextInput
                        value={editDevice?.update_version || ""}
                        onChange={(e) =>
                          handleEditChange("update_version", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Update Filename" />
                      <TextInput
                        value={editDevice?.update_filename || ""}
                        onChange={(e) =>
                          handleEditChange("update_filename", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Update Try Counter" />
                      <TextInput
                        type="number"
                        value={editDevice?.update_try_counter ?? ""}
                        onChange={(e) =>
                          handleEditChange(
                            "update_try_counter",
                            Number(e.target.value)
                          )
                        }
                      />
                    </div>

                    <div className={styles.sectionHeader}>
                      <h3>Operation</h3>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Pulse Frequency" />
                      <TextInput
                        type="number"
                        value={editDevice?.pulse_frequency ?? ""}
                        onChange={(e) =>
                          handleEditChange(
                            "pulse_frequency",
                            Number(e.target.value)
                          )
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Deployment Frequency" />
                      <TextInput
                        type="number"
                        value={editDevice?.deployment_frequency ?? ""}
                        onChange={(e) =>
                          handleEditChange(
                            "deployment_frequency",
                            Number(e.target.value)
                          )
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Sensitivity" />
                      <select
                        value={editDevice?.sensitivity || ""}
                        onChange={(e) =>
                          handleEditChange("sensitivity", e.target.value)
                        }
                      >
                        <option value="" disabled>Select sensitivity</option>
                        <option value="DISABLED">DISABLED</option>
                        <option value="LOW">LOW</option>
                        <option value="LOW_MEDIUM">LOW_MEDIUM</option>
                        <option value="MEDIUM">MEDIUM</option>
                        <option value="MEDIUM_HIGH">MEDIUM_HIGH</option>
                        <option value="HIGH">HIGH</option>
                        <option value="EXTREME_HIGH">EXTREME_HIGH</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Cell Tower ID" />
                      <TextInput
                        type="number"
                        value={editDevice?.cell_tower_id ?? ""}
                        onChange={(e) =>
                          handleEditChange("cell_tower_id", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="Cell Accuracy" />
                      <TextInput
                        type="number"
                        value={editDevice?.cell_accuracy ?? ""}
                        onChange={(e) =>
                          handleEditChange("cell_accuracy", Number(e.target.value))
                        }
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <Label text="AI Key" />
                      <TextInput
                        value={editDevice?.ai_key || ""}
                        onChange={(e) =>
                          handleEditChange("ai_key", e.target.value)
                        }
                      />
                    </div>

                    <div className={styles.formFooter}>
                      {saveError && (
                        <div className={styles.error}>{saveError}</div>
                      )}
                      {saveSuccess && (
                        <div className={styles.success}>Device updated!</div>
                      )}
                    </div>
                  </form>
                </div>
              </Modal>
            )}
          </div>
        )}
      </Frame>
    </>
  );
}
